import type {
  IChatSettings,
  ICreateProjectBody,
  ICreateProjectRoleParams,
  IDeleteProjectRoleParams,
  IEditProjectParams,
  IInviteToProjectParams,
  IParsedProject,
  IProject,
  IProjectList,
  IProjectMember,
  IProjectRole,
  IRemoveProjectMemberParams,
  IUpdateChatSettingsParams,
  IUpdateProjectMemberRoleParams,
  IUpdateProjectRoleParams,
  TProjectPermissionsMap,
} from "../types";
import type { ApiClient } from "./client";

export class ProjectApi {
  constructor(private apiClient: ApiClient) {}

  async getProjects(): Promise<IProjectList> {
    const response = await this.apiClient.get<IProjectList>("/projects");
    return response.data;
  }

  async getProjectsByOrganization(
    organizationUuid: string,
  ): Promise<IParsedProject[]> {
    const response = await this.apiClient.get<IParsedProject[]>(
      `/projects/${organizationUuid}`,
    );
    return response.data;
  }

  async create(data: ICreateProjectBody): Promise<IProject> {
    const response = await this.apiClient.post<IProject>("/projects", data);
    return response.data;
  }

  async edit({ uuid, data }: IEditProjectParams): Promise<IProject> {
    const response = await this.apiClient.put<IProject>(
      `/projects/${uuid}`,
      data,
    );
    return response.data;
  }

  async delete(uuid: string): Promise<void> {
    await this.apiClient.delete(`/projects/${uuid}`);
  }

  async leave(uuid: string): Promise<void> {
    await this.apiClient.post(`/projects/${uuid}/leave`);
  }

  async getMembers(uuid: string): Promise<IProjectMember[]> {
    const response = await this.apiClient.get<IProjectMember[]>(
      `/projects/${uuid}/members`,
    );
    return response.data;
  }

  async inviteToProject({ uuid, data }: IInviteToProjectParams): Promise<void> {
    await this.apiClient.post(`/projects/${uuid}/invite`, data);
  }

  async removeMember({
    uuid,
    memberUuid,
  }: IRemoveProjectMemberParams): Promise<void> {
    await this.apiClient.post(`/projects/${uuid}/remove-member`, {
      memberUuid,
    });
  }

  async updateMemberRole({
    uuid,
    memberUuid,
    data,
  }: IUpdateProjectMemberRoleParams): Promise<void> {
    await this.apiClient.patch(`/projects/${uuid}/update-member-role`, {
      memberUuid,
      roleUuid: data.roleUuid,
    });
  }

  async getRoles(uuid: string): Promise<IProjectRole[]> {
    const response = await this.apiClient.get<IProjectRole[]>(
      `/projects/${uuid}/roles`,
    );
    return response.data;
  }

  async createRole({
    uuid,
    data,
  }: ICreateProjectRoleParams): Promise<IProjectRole> {
    const response = await this.apiClient.post<IProjectRole>(
      `/projects/${uuid}/roles`,
      data,
    );
    return response.data;
  }

  async updateRole({
    uuid,
    roleUuid,
    data,
  }: IUpdateProjectRoleParams): Promise<IProjectRole> {
    // NOTE: Backend uses PATCH for role updates
    const response = await this.apiClient.patch<IProjectRole>(
      `/projects/${uuid}/roles/${roleUuid}`,
      data,
    );
    return response.data;
  }

  async deleteRole({
    uuid,
    roleUuid,
  }: IDeleteProjectRoleParams): Promise<void> {
    await this.apiClient.delete(`/projects/${uuid}/roles/${roleUuid}`);
  }

  async getProjectPermissions(): Promise<TProjectPermissionsMap> {
    const response = await this.apiClient.get<TProjectPermissionsMap>(
      "/projects/permissions",
    );
    return response.data;
  }

  async getProjectChatSettings(): Promise<IChatSettings> {
    const response = await this.apiClient.get<IChatSettings>(
      "/projects/chat-settings",
    );

    return response.data;
  }

  async updateProjectChatSettings({
    organizationUuid,
    body,
  }: IUpdateChatSettingsParams): Promise<IChatSettings> {
    const response = await this.apiClient.patch<IChatSettings>(
      `/organizations/${organizationUuid}/chat-settings`,
      body,
    );

    return response.data;
  }
}
